import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ScanLine, Search, LogOut, CheckCircle2, ArrowLeft, User, AlertTriangle, Keyboard } from "lucide-react";

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "");

export default function ScanPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("scan");
  const [caseload, setCaseload] = useState([]);
  const [search, setSearch] = useState("");
  const [manualCode, setManualCode] = useState("");
  const [result, setResult] = useState(null);
  const [service, setService] = useState("");
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(null);
  const [cameraError, setCameraError] = useState(false);
  const [brand, setBrand] = useState(null);
  const scannerRef = useRef(null);
  const processingRef = useRef(false);

  useEffect(() => {
    api.get("/clients").then((r) => setBrand(r.data[0] || null)).catch(() => {});
  }, []);

  const loadCaseload = useCallback(() => {
    api.get("/caseload").then((r) => setCaseload(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadCaseload(); }, [loadCaseload]);

  const handleCode = useCallback(async (code) => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      const { data } = await api.post("/scan", { code });
      setResult(data);
      setService(data.student.services[0] || "Related Service");
      if (navigator.vibrate) navigator.vibrate(80);
    } catch (e) {
      toast.error(apiError(e));
      setTimeout(() => { processingRef.current = false; }, 1500);
    }
  }, []);

  useEffect(() => {
    const swallow = (e) => {
      const msg = String(e?.reason?.message || e?.reason || "");
      if (/play\(\)|media|camera|video/i.test(msg)) e.preventDefault();
    };
    window.addEventListener("unhandledrejection", swallow);
    return () => window.removeEventListener("unhandledrejection", swallow);
  }, []);

  useEffect(() => {
    if (mode !== "scan" || result || success) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError(true);
      return;
    }
    let scanner = new Html5Qrcode("qr-reader");
    scannerRef.current = scanner;
    const startPromise = scanner
      .start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        (text) => handleCode(text),
        () => {}
      )
      .catch(() => {
        if (!cancelled) setCameraError(true);
      });
    let cancelled = false;
    return () => {
      cancelled = true;
      startPromise.then(() => {
        if (scanner.isScanning) return scanner.stop();
      }).catch(() => {});
    };
  }, [mode, result, success, handleCode]);

  const closeSheet = () => {
    setResult(null);
    processingRef.current = false;
    loadCaseload();
  };

  const doPickup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/verifications/pickup", { student_id: result.student.id, service });
      setSuccess({ action: "picked up", name: data.student_name, time: data.pickup_time });
      setResult(null);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); processingRef.current = false; loadCaseload(); }
  };

  const doReturn = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/verifications/return", { student_id: result.student.id });
      setSuccess({ action: "returned", name: data.student_name, time: data.return_time, duration: data.duration_minutes });
      setResult(null);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); processingRef.current = false; loadCaseload(); }
  };

  const filtered = caseload.filter((s) =>
    `${s.first_name} ${s.last_name} ${s.student_id}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="flex min-h-screen flex-col bg-slate-950 text-white" data-testid="scan-page">
      <header className="flex items-center justify-between border-b border-slate-800 px-4 py-3">
        <div className="flex items-center gap-2.5">
          {brand?.logo_url && (
            <img src={brand.logo_url} alt={brand.name} className="h-8 w-8 rounded-lg bg-white object-contain p-0.5" data-testid="scan-client-logo" />
          )}
          <div>
            <div className="font-['Inter'] text-lg font-bold tracking-tight">{brand ? brand.name : "TAGVerify"}</div>
            <div className="text-xs text-slate-400" data-testid="provider-name">{user?.name} · Provider</div>
          </div>
        </div>
        <button onClick={async () => { await logout(); navigate("/login"); }} data-testid="scan-logout-button"
          className="flex items-center gap-1.5 rounded-full border border-slate-700 px-3 py-1.5 text-xs text-slate-300 transition-colors duration-200 hover:bg-slate-800">
          <LogOut size={13} /> Sign out
        </button>
      </header>

      <div className="flex border-b border-slate-800">
        {[{ id: "scan", label: "Scan QR", icon: ScanLine }, { id: "search", label: "Search", icon: Search }].map((t) => (
          <button key={t.id} onClick={() => { setMode(t.id); setCameraError(false); }} data-testid={`mode-${t.id}-tab`}
            className={`flex flex-1 items-center justify-center gap-2 py-3 text-sm font-semibold transition-colors duration-200 ${
              mode === t.id ? "border-b-2 border-teal-400 text-white" : "text-slate-500"
            }`}>
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      <main className="relative flex flex-1 flex-col">
        {mode === "scan" && !result && !success && (
          <div className="relative flex flex-1 flex-col items-center justify-center p-6">
            {!cameraError ? (
              <>
                <div id="qr-reader" className="h-72 w-72 overflow-hidden rounded-2xl bg-slate-900" />
                <p className="mt-6 text-sm text-slate-400">Point camera at a student's TAGVerify badge</p>
              </>
            ) : (
              <div className="text-center">
                <AlertTriangle size={32} className="mx-auto mb-3 text-amber-500" />
                <p className="text-sm text-slate-400">Camera unavailable. Use manual entry below or the Search tab.</p>
              </div>
            )}
            <div className="mt-8 w-full max-w-sm">
              <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                <Keyboard size={12} /> Manual entry
              </div>
              <div className="flex gap-2">
                <input value={manualCode} onChange={(e) => setManualCode(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && manualCode && handleCode(manualCode)}
                  placeholder="Student ID (e.g. KA-1001)" data-testid="manual-code-input"
                  className="flex-1 rounded-full border border-slate-700 bg-slate-900 px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500" />
                <button onClick={() => manualCode && handleCode(manualCode)} data-testid="manual-code-submit"
                  className="rounded-full bg-teal-600 px-5 text-sm font-semibold transition-colors duration-200 hover:bg-teal-500">Go</button>
              </div>
            </div>
          </div>
        )}

        {mode === "search" && !result && !success && (
          <div className="flex-1 p-4">
            <div className="relative mb-4">
              <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search caseload by name or ID..."
                data-testid="caseload-search-input"
                className="w-full rounded-full border border-slate-700 bg-slate-900 py-2.5 pl-10 pr-4 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
            <div className="space-y-2">
              {filtered.map((s) => (
                <button key={s.id} onClick={() => handleCode(s.student_id)} data-testid={`caseload-student-${s.student_id}`}
                  className="flex w-full items-center justify-between rounded-xl border border-slate-800 bg-slate-900 p-4 text-left transition-colors duration-200 hover:border-slate-600">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-800 text-slate-300">
                      <User size={18} />
                    </div>
                    <div>
                      <div className="font-semibold">{s.first_name} {s.last_name}</div>
                      <div className="font-mono text-xs text-slate-500">{s.student_id} · Grade {s.grade}</div>
                    </div>
                  </div>
                  <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                    s.status === "in_service" ? "bg-amber-500/15 text-amber-400" : "bg-green-500/15 text-green-400"
                  }`}>
                    {s.status === "in_service" ? "In Service" : "Available"}
                  </span>
                </button>
              ))}
              {filtered.length === 0 && <div className="py-10 text-center text-sm text-slate-500">No students in your caseload match</div>}
            </div>
          </div>
        )}

        {result && (
          <div className="fixed inset-0 z-50 flex flex-col justify-end bg-slate-950/80 backdrop-blur-sm">
            <div className="sheet-up rounded-t-3xl border-t border-slate-700 bg-slate-900 p-6 pb-10" data-testid="scan-result-sheet">
              <button onClick={closeSheet} data-testid="close-sheet-button"
                className="mb-4 flex items-center gap-1.5 text-sm text-slate-400 transition-colors duration-200 hover:text-white">
                <ArrowLeft size={15} /> Back
              </button>
              <div className="text-center">
                <h2 className="font-['Inter'] text-3xl font-bold tracking-tight" data-testid="scanned-student-name">
                  {result.student.first_name} {result.student.last_name}
                </h2>
                <div className="mt-1 font-mono text-sm text-slate-400">
                  {result.student.student_id} · Grade {result.student.grade} · {result.student.school_name}
                </div>
                {!result.assigned && (
                  <div className="mx-auto mt-3 flex max-w-xs items-center justify-center gap-2 rounded-full bg-red-500/15 px-4 py-2 text-xs font-semibold text-red-400" data-testid="not-assigned-warning">
                    <AlertTriangle size={14} /> You are not an assigned provider
                  </div>
                )}
                {result.active_verification ? (
                  <>
                    <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-amber-500/15 px-4 py-2 text-sm font-semibold text-amber-400">
                      <span className="pulse-dot h-2 w-2 rounded-full bg-amber-400" />
                      Currently In Service · out since {fmtTime(result.active_verification.pickup_time)}
                    </div>
                    <div className="mt-2 text-sm text-slate-400">
                      {result.active_verification.service} with {result.active_verification.provider_name}
                    </div>
                    <button onClick={doReturn} disabled={busy} data-testid="return-button"
                      className="mt-7 h-20 w-full rounded-full bg-[#C7A34A] font-['Inter'] text-2xl font-bold tracking-tight text-slate-900 shadow-lg shadow-[#C7A34A]/20 transition-colors duration-200 hover:bg-[#b8933d] disabled:opacity-50">
                      {busy ? "..." : "RETURN"}
                    </button>
                  </>
                ) : (
                  <>
                    <div className="mt-5 space-y-2">
                      <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Service</div>
                      <div className="flex flex-wrap justify-center gap-2">
                        {(result.student.services.length ? result.student.services : ["Related Service"]).map((svc) => (
                          <button key={svc} onClick={() => setService(svc)} data-testid={`service-option-${svc.toLowerCase().replace(/ /g, "-")}`}
                            className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                              service === svc ? "border-teal-400 bg-teal-400/15 text-teal-200" : "border-slate-700 text-slate-400"
                            }`}>
                            {svc}
                          </button>
                        ))}
                      </div>
                      <div className="pt-1 text-sm text-slate-400">
                        Provider: <span className="text-white">{result.provider_name}</span>
                        {result.provider_title && <span> · {result.provider_title}</span>}
                      </div>
                    </div>
                    <button onClick={doPickup} disabled={busy} data-testid="pickup-button"
                      className="mt-7 h-20 w-full rounded-full bg-[#0D6E6E] font-['Inter'] text-2xl font-bold tracking-tight shadow-lg shadow-teal-900/40 transition-colors duration-200 hover:bg-[#0b5d5d] disabled:opacity-50">
                      {busy ? "..." : "PICK UP"}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {success && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/90 p-6">
            <div className="fade-up text-center" data-testid="success-screen">
              <div className="success-pop success-ring mx-auto flex h-28 w-28 items-center justify-center rounded-full bg-emerald-500/15">
                <CheckCircle2 size={64} className="text-emerald-400" />
              </div>
              <h2 className="mt-4 font-['Inter'] text-2xl font-bold tracking-tight">{success.name}</h2>
              <p className="mt-1 text-slate-400">
                Verified {success.action} at <span className="font-mono text-white">{fmtTime(success.time)}</span>
                {success.duration != null && <> · {success.duration} min in service</>}
              </p>
              <button onClick={() => { setSuccess(null); processingRef.current = false; }} data-testid="scan-next-button"
                className="mt-8 h-14 rounded-full bg-white px-10 font-['Inter'] text-lg font-bold text-slate-900 transition-colors duration-200 hover:bg-slate-200">
                Scan next student
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
